import React from "react";
import { StatusBadge } from "@/components/StatusBadge";
import { waLink } from "@/lib/api";
import { ArrowDown, MapPin, Phone, MessageCircle, Clock } from "lucide-react";

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export const OrderCard = ({ order, driverNumber, isDriver = false, actions = null }) => {
  // Customer contacts the driver on WhatsApp; driver contacts the customer.
  const targetNumber = isDriver ? order.contact_phone : driverNumber;
  const waText = `Bonjour, à propos de la commande CHAP CHAP #${order.id.slice(0, 8)} — ${order.service}. De: ${order.from_address}. Vers: ${order.to_address}. Montant en espèces: ${order.price}€.`;
  const canWhatsapp = ["Accepté", "En cours", "Livré"].includes(order.status) && targetNumber;

  return (
    <div data-testid={`order-card-${order.id}`} className="fade-up rounded-2xl border border-[#1F293D] bg-[#151b2b] p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="rounded-lg bg-[#ff6a00]/10 px-2 py-0.5 text-xs font-bold text-[#ff6a00]">
            {order.service}
          </span>
          <span className="font-mono text-xs text-slate-500">#{order.id.slice(0, 8)}</span>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {isDriver && (
        <div className="mb-2 text-xs text-slate-400">
          {order.user_role === "restaurant" ? "Restaurant" : "Client"} : {order.user_name || order.user_email}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
          <div className="text-xs text-slate-300">
            <span className="text-slate-500">Enlèvement · </span>{order.from_address}
          </div>
        </div>
        <div className="ml-2"><ArrowDown className="h-3 w-3 text-slate-600" /></div>
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#ff6a00]" />
          <div className="text-xs text-slate-300">
            <span className="text-slate-500">Livraison · </span>{order.to_address}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-[#1F293D] pt-3">
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="font-mono">{order.distance_km} km</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{order.duration_min} min</span>
        </div>
        <div className="font-mono text-xl font-extrabold text-[#ff6a00]">{order.price}€</div>
      </div>

      <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtTime(order.created_at)}</span>
        {order.contact_phone && (
          <a href={`tel:${order.contact_phone}`} className="flex items-center gap-1 text-slate-400 hover:text-[#ff6a00]">
            <Phone className="h-3 w-3" />{order.contact_phone}
          </a>
        )}
      </div>

      {isDriver && order.contact_phone && ["Accepté", "En cours"].includes(order.status) && (
        <a
          data-testid={`call-btn-${order.id}`}
          href={`tel:${order.contact_phone}`}
          className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-[#ff6a00]/50 bg-[#ff6a00]/10 py-2.5 text-sm font-bold text-[#ff6a00] transition-colors hover:bg-[#ff6a00]/20"
        >
          <Phone className="h-4 w-4" /> Appeler le client
        </a>
      )}

      {canWhatsapp && (
        <a
          data-testid={`whatsapp-btn-${order.id}`}
          href={waLink(targetNumber, waText)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-500"
        >
          <MessageCircle className="h-4 w-4" />
          {isDriver ? "Contacter le client" : "Contacter le livreur"}
        </a>
      )}
      {["Accepté", "En cours"].includes(order.status) && !targetNumber && (
        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-center text-[11px] text-amber-300">
          {isDriver ? "Numéro client manquant" : "Numéro WhatsApp du livreur non configuré"}
        </div>
      )}

      {actions}
    </div>
  );
};
